// Copyright 2024 Sadiant Inc. All Rights Reserved. This software is subject to a license agreement. Unauthorized or unlicensed use is prohibited.
import { Routes } from '@angular/router';

export const appRoutes: Routes = [
  {
    path: '',
    loadComponent: () => import('./views/home/home.component').then(c => c.HomeComponent),
    data: {
      seo: {
        title: 'Healthcare Staffing App for Facilities & Clinicians | Sadiant Health',
        description: 'Sadiant Health connects hospitals and facilities with qualified nurses and allied health clinicians. On-demand per diem and PRN shifts, filled fast.',
        keywords: 'on-demand healthcare staffing, per diem staffing app, healthcare staffing platform, PRN nursing jobs, allied health staffing',
        url: '/',
        canonical: '/',
        image: '/assets/og/sadiant-home-og.jpg',
        robots: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
        geoRegion: 'US-TX',
        geoPlacename: 'Fort Worth',
        geoPosition: '32.7555;-97.3308',
        ICBM: '32.7555, -97.3308',
        // JSON-LD uses production URLs — Google crawls production only
        jsonLd: [
          {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Sadiant Health',
            legalName: 'Sadiant Health LLC',
            url: 'https://www.sadianthealth.com/',
            logo: 'https://www.sadianthealth.com/assets/images/sadiant-logo.png',
            description: 'Sadiant Health connects hospitals and facilities with qualified nurses and allied health clinicians. On-demand per diem and PRN shifts, filled fast.',
            foundingDate: '2016',
            founders: [{ '@type': 'Person', name: 'Sarah Snetzer' }],
            address: {
              '@type': 'PostalAddress',
              streetAddress: '2833 Crockett Street, Suite 112',
              addressLocality: 'Fort Worth',
              addressRegion: 'TX',
              postalCode: '76107',
              addressCountry: 'US'
            },
            contactPoint: {
              '@type': 'ContactPoint',
              email: 'hello@sadianthealth.com',
              contactType: 'customer service',
              areaServed: 'US'
            },
            sameAs: ['https://www.linkedin.com/company/sadiant-health', 'https://www.facebook.com/sadianthealth', 'https://twitter.com/sadianthealth', 'https://www.instagram.com/sadianthealth']
          },
          {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'Sadiant Health',
            url: 'https://www.sadianthealth.com/',
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://www.sadianthealth.com/search?q={search_term_string}',
              'query-input': 'required name=search_term_string'
            }
          }
        ]
      }
    }
  },
  {
    path: 'about-us',
    children: [
      {
        path: '',
        loadComponent: () => import('./views/about-us/about-us.component').then(c => c.AboutUsComponent),
        data: {
          seo: {
            title: 'About Sadiant Health | Nurse-Founded Staffing Co.',
            description: 'Learn how Sadiant Health, a nurse-founded staffing company, delivers flexible per diem staffing for nurses and allied health clinicians.',
            keywords: 'about Sadiant Health, nurse-founded company, Fort Worth staffing company, healthcare workforce experts, Sadiant founders',
            url: '/about-us',
            canonical: '/about-us',
            image: '/assets/og/sadiant-about-og.jpg',
            robots: 'index, follow',
            jsonLd: [
              {
                '@context': 'https://schema.org',
                '@type': 'AboutPage',
                url: 'https://www.sadianthealth.com/about-us',
                name: 'About Sadiant Health',
                description: 'Learn how Sadiant Health, a nurse-founded staffing company, delivers flexible per diem staffing for nurses and allied health clinicians.',
                mainEntity: {
                  '@type': 'Organization',
                  name: 'Sadiant Health',
                  url: 'https://www.sadianthealth.com/',
                  foundingDate: '2016',
                  foundingLocation: 'Fort Worth, TX, USA'
                }
              }
            ]
          }
        }
      }
    ]
  },
  {
    path: 'facilities',
    loadComponent: () => import('./views/facilities/facilities.component').then(c => c.FacilitiesComponent),
    data: {
      seo: {
        title: 'Healthcare Staffing for Hospitals & Facilities | Sadiant Health',
        description: 'Reliable per diem and PRN staffing for hospitals, SNFs, clinics, and care facilities. Fill open nurse and allied health shifts on demand.',
        keywords: 'hospital staffing solutions, healthcare staffing agency, per diem staffing, travel nurse staffing, allied health staffing, hospital workforce solutions, SNF staffing, long term care staffing, clinic staffing services',
        url: '/facilities',
        canonical: '/facilities',
        image: '/assets/og/sadiant-facilities-og.jpg',
        robots: 'index, follow, max-image-preview:large',
        jsonLd: [
          {
            '@context': 'https://schema.org',
            '@type': 'Service',
            serviceType: 'On-Demand Nurse Staffing',
            name: 'Sadiant Health Nurse Staffing for Facilities',
            provider: {
              '@type': 'Organization',
              name: 'Sadiant Health',
              url: 'https://www.sadianthealth.com/'
            },
            areaServed: { '@type': 'Country', name: 'United States' },
            description: 'On-demand per diem nurse staffing for hospitals, skilled nursing facilities, ambulatory surgery centers, long-term care facilities and home health agencies.',
            offers: {
              '@type': 'Offer',
              priceCurrency: 'USD',
              description: 'Transparent per-shift pricing with no long-term contracts'
            },
            audience: { '@type': 'BusinessAudience', audienceType: 'Healthcare Facilities' }
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.sadianthealth.com/' },
              { '@type': 'ListItem', position: 2, name: 'For Facilities', item: 'https://www.sadianthealth.com/facilities' }
            ]
          }
        ]
      }
    }
  },
  {
    path: 'clinicians',
    loadComponent: () => import('./views/clinicians/clinicians.component').then(c => c.CliniciansComponent),
    data: {
      seo: {
        title: 'Per Diem Healthcare Jobs | Pick Your Shifts | Sadiant',
        description: 'Explore flexible per diem and PRN jobs for nurses and allied health clinicians. Pick the shifts that fit your schedule and career goals.',
        keywords: 'per diem nursing jobs, PRN nursing jobs, healthcare jobs near me, allied health jobs, travel healthcare jobs, flexible nursing jobs, RN jobs, CNA jobs, respiratory therapist jobs',
        url: '/clinicians',
        canonical: '/clinicians',
        image: '/assets/og/sadiant-clinicians-og.jpg',
        robots: 'index, follow, max-image-preview:large',
        appleItunesApp: 'app-id=YOUR_IOS_APP_ID',
        googlePlayApp: 'app-id=com.sadianthealth.app',
        jsonLd: [
          {
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'Sadiant Health',
            operatingSystem: 'iOS, Android',
            applicationCategory: 'BusinessApplication',
            description: 'On-demand per diem nursing job app connecting RNs, LVNs, CNAs and allied health professionals with local healthcare facility shifts.',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
            aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.9', bestRating: '5', ratingCount: '500' }
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.sadianthealth.com/' },
              { '@type': 'ListItem', position: 2, name: 'For Clinicians', item: 'https://www.sadianthealth.com/clinicians' }
            ]
          }
        ]
      }
    }
  },
  {
    path: 'apply',
    loadComponent: () => import('./views/apply/apply.component').then(c => c.ApplyComponent),
    data: {
      seo: {
        title: 'Apply Now | Per Diem Healthcare Jobs — Sadiant Health',
        description: 'Apply to Sadiant Health and start picking per diem shifts. RN, LVN, CNA, and allied health roles with flexible, self-scheduled hours.',
        keywords: 'apply per diem jobs, healthcare job application, RN LVN CNA apply, allied health apply, join Sadiant Health',
        url: '/apply',
        canonical: '/apply',
        image: '/assets/og/sadiant-clinicians-og.jpg',
        robots: 'noindex, nofollow'
      }
    }
  },
  // {
  //   path: 'news',
  //   children: [
  //     {
  //       path: '',
  //       loadComponent: () => import('./views/news/news.component').then(c => c.NewsComponent),
  //       data: {
  //         seo: {
  //           title: 'News & Press Releases | Sadiant Health Updates',
  //           description: 'Latest announcements from Sadiant Health: leadership updates, platform releases, new markets, industry insights on on-demand healthcare staffing.',
  //           keywords: 'Sadiant Health news, healthcare staffing news, nurse staffing updates, Sadiant press releases',
  //           url: '/news',
  //           canonical: '/news',
  //           image: '/assets/og/sadiant-news-og.jpg',
  //           robots: 'index, follow',
  //           jsonLd: [
  //             {
  //               '@context': 'https://schema.org',
  //               '@type': 'BreadcrumbList',
  //               itemListElement: [
  //                 { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.sadianthealth.com/' },
  //                 { '@type': 'ListItem', position: 2, name: 'News', item: 'https://www.sadianthealth.com/news' }
  //               ]
  //             }
  //           ]
  //         }
  //       }
  //     }
  //     // { path: '2021-05-04', loadComponent: () => import('./views/news/news-2021-05-04/news-2021-05-04.component').then(c => c.News20210504Component) },
  //     // { path: '2020-10-20', loadComponent: () => import('./views/news/news-2020-10-20/news-2020-10-20.component').then(c => c.News20201020Component) },
  //     // { path: '2020-08-31', loadComponent: () => import('./views/news/news-2020-08-31/news-2020-08-31.component').then(c => c.News20200831Component) },
  //     // { path: '2020-06-23', loadComponent: () => import('./views/news/news-2020-06-23/news-2020-06-23.component').then(c => c.News20200623Component) },
  //     // { path: '2020-06-15', loadComponent: () => import('./views/news/news-2020-06-15/news-2020-06-15.component').then(c => c.News20200615Component) },
  //     // { path: '2020-05-26', loadComponent: () => import('./views/news/news-2020-05-26/news-2020-05-26.component').then(c => c.News20200526Component) },
  //     // { path: '2020-04-22', loadComponent: () => import('./views/news/news-2020-04-22/news-2020-04-22.component').then(c => c.News20200422Component) },
  //     // { path: '2019-08-26', loadComponent: () => import('./views/news/news-2019-08-26/news-2019-08-26.component').then(c => c.News20190826Component) },
  //     // { path: '2019-07-17', loadComponent: () => import('./views/news/news-2019-07-17/news-2019-07-17.component').then(c => c.News20190717Component) },
  //     // { path: '2018-10-01', loadComponent: () => import('./views/news/news-2018-10-01/news-2018-10-01.component').then(c => c.News20181001Component) },
  //     // { path: '2018-05-05', loadComponent: () => import('./views/news/news-2018-05-05/news-2018-05-05.component').then(c => c.News20180505Component) },
  //     // { path: '2018-02-05', loadComponent: () => import('./views/news/news-2018-02-05/news-2018-02-05.component').then(c => c.News20180205Component) },
  //     // { path: '2017-12-10', loadComponent: () => import('./views/news/news-2017-12-10/news-2017-12-10.component').then(c => c.News20171210Component) }
  //   ]
  // },
  // {
  //   path: 'news/:slug',
  //   loadComponent: () => import('./views/news/news-detail/news-detail.component').then(c => c.NewsDetailComponent)
  // },
  {
    path: 'contact',
    loadComponent: () => import('./views/contact/contact.component').then(c => c.ContactComponent),
    data: {
      seo: {
        title: 'Contact Sadiant Health | Book a Demo or Support',
        description: 'Contact Sadiant Health for staffing solutions, clinician support, or partnership opportunities. Book a demo for your facility or reach clinician support at hello@sadianthealth.com — we respond within 1 business day.',
        keywords: 'contact Sadiant Health, book a demo, healthcare staffing support, Sadiant Fort Worth, contact Sadiant Health, healthcare staffing support, staffing inquiry, book a staffing consultation, healthcare workforce support',
        url: '/contact',
        canonical: '/contact',
        image: '/assets/og/sadiant-contact-og.jpg',
        robots: 'index, follow',
        geoRegion: 'US-TX',
        geoPlacename: 'Fort Worth',
        geoPosition: '32.7555;-97.3308',
        ICBM: '32.7555, -97.3308',
        jsonLd: [
          {
            '@context': 'https://schema.org',
            '@type': 'ContactPage',
            url: 'https://www.sadianthealth.com/contact',
            mainEntity: {
              '@type': 'LocalBusiness',
              name: 'Sadiant Health',
              image: 'https://www.sadianthealth.com/assets/images/logo.png',
              url: 'https://www.sadianthealth.com/',
              telephone: '+1-817-562-7256',
              email: 'hello@sadianthealth.com',
              address: {
                '@type': 'PostalAddress',
                streetAddress: '2833 Crockett Street, Suite 112',
                addressLocality: 'Fort Worth',
                addressRegion: 'TX',
                postalCode: '76107',
                addressCountry: 'US'
              },
              geo: { '@type': 'GeoCoordinates', latitude: 32.7555, longitude: -97.3308 },
              openingHoursSpecification: [
                {
                  '@type': 'OpeningHoursSpecification',
                  dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
                  opens: '08:00',
                  closes: '18:00'
                }
              ],
              priceRange: '$$'
            }
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'How quickly can you fill per diem nursing shifts?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'We specialize in immediate placement — most facilities fill open nursing shifts within hours of posting, not days or weeks like traditional nurse staffing agencies.'
                }
              },
              {
                '@type': 'Question',
                name: 'What types of clinicians does Sadiant staff?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'We place RNs, LVNs, CNAs, surgical technologists, respiratory therapists, medical assistants and allied health professionals across hospitals, SNFs, clinics and ambulatory surgery centers.'
                }
              },
              {
                '@type': 'Question',
                name: 'Is the Sadiant per diem nursing app free?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes — the Sadiant app is completely free for clinicians to download, browse per diem shifts near you, and apply. No agency fees, ever.'
                }
              }
            ]
          }
        ]
      }
    }
  },
  {
    path: 'sign-up',
    loadComponent: () => import('./views/sign-up/sign-up.component').then(c => c.SignUpComponent),
    data: {
      seo: {
        title: 'Sign Up for Per Diem Nursing Shifts | Sadiant Health',
        description: 'Create your Sadiant Health account in minutes and start booking per diem shifts that fit your schedule.',
        keywords: 'sign up nursing app, create nursing account, per diem nurse registration, join Sadiant',
        url: '/sign-up',
        canonical: '/sign-up',
        image: '/assets/og/sadiant-signup-og.jpg',
        robots: 'index, follow',
        jsonLd: [
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Sign Up | Sadiant Health',
            url: 'https://www.sadianthealth.com/sign-up',
            description: 'Join Sadiant Health and start picking up per diem RN, LVN and CNA shifts near you.',
            potentialAction: {
              '@type': 'RegisterAction',
              target: 'https://www.sadianthealth.com/sign-up',
              name: 'Sign up for Sadiant Health'
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.sadianthealth.com/' },
                { '@type': 'ListItem', position: 2, name: 'Sign Up', item: 'https://www.sadianthealth.com/sign-up' }
              ]
            }
          }
        ]
      }
    }
  },
  {
    path: 'terms-of-service',
    loadComponent: () => import('./views/terms-of-service/terms-of-service.component').then(c => c.TermsOfServiceComponent),
    data: {
      seo: {
        title: 'Terms of Service | Sadiant Health',
        description: 'Read the Terms of Service for Sadiant Health — the on-demand nurse staffing platform connecting clinicians with US healthcare facilities.',
        url: '/terms-of-service',
        canonical: '/terms-of-service',
        image: '/assets/og/sadiant-home-og.jpg',
        robots: 'index, follow'
      }
    }
  },
  {
    path: 'privacy-policy',
    loadComponent: () => import('./views/privacy-policy/privacy-policy.component').then(c => c.PrivacyPolicyComponent),
    data: {
      seo: {
        title: 'Privacy Policy | Sadiant Health',
        description: 'How Sadiant Health collects, uses and protects personal and health information from nurses, clinicians and healthcare facility users.',
        url: '/privacy-policy',
        canonical: '/privacy-policy',
        image: '/assets/og/sadiant-home-og.jpg',
        robots: 'index, follow'
      }
    }
  },
  {
    path: 'events',
    loadComponent: () => import('./views/conference-events/conference-events.component').then(c => c.ConferenceEventsComponent),
    data: {
      seo: {
        title: 'Healthcare Staffing Events & Conferences | Sadiant Health',
        description: 'Meet the Sadiant Health team at upcoming healthcare staffing conferences and industry events. Connect with us to learn how on-demand nurse staffing works for your facility.',
        keywords: 'Sadiant Health events, healthcare staffing conference, nurse staffing expo, Sadiant conference',
        url: '/events',
        canonical: '/events',
        image: '/assets/og/sadiant-home-og.jpg',
        robots: 'noindex, follow'
      }
    }
  },
  {
    path: 'tennessee',
    loadComponent: () => import('./views/tennessee/tennessee.component').then(c => c.TennesseeComponent),
    data: {
      seo: {
        title: 'Per Diem Nursing Jobs in Tennessee | Sadiant Health',
        description: 'RNs, LVNs, CNAs — pick up flexible per diem shifts at healthcare facilities across Tennessee with Sadiant Health. Apply today and earn a $1,000 welcome bonus.',
        keywords: 'per diem nursing jobs Tennessee, RN LVN CNA jobs Tennessee, nurse staffing Tennessee, PRN nursing Tennessee',
        url: '/tennessee',
        canonical: '/tennessee',
        image: '/assets/og/sadiant-clinicians-og.jpg',
        robots: 'index, follow'
      }
    }
  },
  {
    path: 'tyler',
    loadComponent: () => import('./views/tyler/tyler.component').then(c => c.TylerComponent),
    data: {
      seo: {
        title: 'Per Diem Nursing Jobs in Tyler, TX | Sadiant Health',
        description: 'Per diem RN, LVN and CNA shifts in Tyler, Texas and surrounding East Texas facilities. Join Sadiant Health for flexible nursing jobs with transparent pay and weekly direct deposit.',
        keywords: 'per diem nursing jobs Tyler TX, RN LVN CNA jobs Tyler Texas, nurse staffing Tyler, East Texas nursing jobs',
        url: '/tyler',
        canonical: '/tyler',
        image: '/assets/og/sadiant-clinicians-og.jpg',
        robots: 'index, follow'
      }
    }
  },
  {
    path: 'reference/:credentialExtId',
    loadComponent: () => import('./views/reference-request/reference-request.component').then(c => c.ReferenceRequestComponent)
  },
  { path: '**', redirectTo: '/', pathMatch: 'full' }
];
